import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectTrigger, SelectValue } from "./ui/select";
import { assignJob, returnJobToQueue, api } from "../lib/api";
import { toast } from "sonner";
import { formatDistance, formatDuration, cn } from "../lib/utils";
import { Loader2, Route, Trophy, Undo2, CalendarClock } from "lucide-react";
import {
  ScheduledAtPicker,
  TruckOption,
  toLocalInput,
} from "./assignDialog/AssignDialogParts";
import { wallToDate, wallToIso } from "../lib/tenantTime";
import { tenantLocale, tenantTz } from "../lib/company";

// Sources that are urgent-turnaround by contract with the partner — same-day
// scheduling only. Reassign to a different truck is fine, but pushing the job
// to tomorrow isn't. Kept in one place so the frontend + any future backend
// enforcement stay aligned.
const PARTNER_URGENT_SOURCES = new Set(["aa_workforms", "aa_sms", "servicenow"]);

export default function AssignDialog({ job, trucks, jobs, open, onOpenChange, onAssigned }) {
  const [truckId, setTruckId] = useState("");
  const [scheduledAt, setScheduledAt] = useState(() => toLocalInput(new Date()));
  const [saving, setSaving] = useState(false);
  const [etas, setEtas] = useState({}); // { truck_id: {distance_m, duration_s} }
  const [etasLoading, setEtasLoading] = useState(false);
  const [lastJobId, setLastJobId] = useState(null);

  const jobIdForEffect = open && job ? job.id : null;

  // Render-phase reset when the dialog opens for a new job (avoids state-in-effect lint).
  if (jobIdForEffect && jobIdForEffect !== lastJobId) {
    setLastJobId(jobIdForEffect);
    setTruckId("");
    setEtas({});
    setEtasLoading(true);
    // Reset schedule to "now" each time the dialog opens — dispatchers usually
    // want immediate dispatch and this saves them clicking through the picker.
    setScheduledAt(toLocalInput(new Date()));
  }

  // Fetch live ETAs whenever the dialog opens for a new job
  useEffect(() => {
    if (!jobIdForEffect) return;
    let cancelled = false;
    api
      .get(`/jobs/${jobIdForEffect}/truck-etas`)
      .then((r) => {
        if (cancelled) return;
        const map = {};
        (r.data?.etas || []).forEach((e) => {
          map[e.truck_id] = e;
        });
        setEtas(map);
      })
      .catch(() => {
        // ETA is best-effort — silent fail, user can still assign
      })
      .finally(() => {
        if (!cancelled) setEtasLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [jobIdForEffect]);

  if (!job) return null;
  const isReassign = Boolean(job.assigned_truck_id);
  const currentTruck = isReassign ? (trucks || []).find((t) => t.id === job.assigned_truck_id) : null;
  // For reassignment, exclude the currently assigned truck — the dispatcher
  // is moving the job AWAY from it. For first-time assignment, show all
  // available/busy trucks.
  const assignable = (trucks || []).filter(
    (t) => (t.status === "available" || t.status === "busy") && t.id !== job.assigned_truck_id,
  );

  const queueSizeFor = (tid) =>
    (jobs || []).filter(
      (j) => j.assigned_truck_id === tid && !["completed", "cancelled"].includes(j.status)
    ).length;

  // Sort trucks by live ETA (ascending). Trucks without an ETA go to the bottom.
  const sortedTrucks = [...assignable].sort((a, b) => {
    const da = etas[a.id]?.duration_s;
    const db = etas[b.id]?.duration_s;
    if (da == null && db == null) return 0;
    if (da == null) return 1;
    if (db == null) return -1;
    return da - db;
  });

  const closestTruckId = sortedTrucks.find((t) => etas[t.id])?.id;
  const selectedQueueSize = truckId ? queueSizeFor(truckId) : 0;
  const selectedEta = truckId ? etas[truckId] : null;

  const submit = async () => {
    if (!truckId) {
      toast.error("Select a truck");
      return;
    }
    setSaving(true);
    try {
      await assignJob(job.id, {
        truck_id: truckId,
        scheduled_at: scheduledAt ? wallToIso(scheduledAt) : null,
      });
      toast.success(isReassign ? "Job reassigned" : "Truck assigned");
      onAssigned?.();
      onOpenChange(false);
      setTruckId("");
    } catch {
      toast.error("Failed to assign");
    } finally {
      setSaving(false);
    }
  };

  const sameDayOnly = PARTNER_URGENT_SOURCES.has(job.source);

  // Return to queue — takes the job off the current truck and puts it back in
  // the unassigned queue. When the schedule picker sits > 2h in the future
  // (and the job isn't a partner-source urgent), sends `book_for` so the
  // backend flips it to `status=booked` for the Tow Booked list instead of
  // dropping it straight into `pending`. Button label + toast reflect the
  // action so the dispatcher sees exactly what will happen.
  const scheduledDate = scheduledAt ? wallToDate(scheduledAt) : null;
  const scheduledMsAhead = scheduledDate ? scheduledDate.getTime() - Date.now() : 0;
  const willBookForLater = !sameDayOnly && scheduledMsAhead > 2 * 60 * 60 * 1000;

  const returnToQueue = async () => {
    if (!job.assigned_truck_id) return;
    setSaving(true);
    try {
      const body = willBookForLater
        ? { book_for: scheduledDate.toISOString() }
        : null;
      await returnJobToQueue(job.id, body);
      toast.success(
        willBookForLater
          ? `Job booked for ${scheduledDate.toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })}`
          : "Job returned to queue"
      );
      onAssigned?.();
      onOpenChange(false);
      setTruckId("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to return to queue");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black tracking-tight">
            {isReassign ? "Reassign Truck" : "Assign Truck"}
          </DialogTitle>
          <DialogDescription>
            {isReassign && currentTruck ? (
              <>
                Move <strong>{job.customer_name}</strong> from{" "}
                <span className="text-amber-700 font-semibold">{currentTruck.name}</span> to a different truck.
              </>
            ) : (
              <>{job.customer_name} · {job.address}</>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <ScheduledAtPicker value={scheduledAt} onChange={setScheduledAt} sameDayOnly={sameDayOnly} />

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="uppercase-label">Assign to truck</Label>
              {etasLoading && (
                <span className="flex items-center gap-1 text-[11px] text-slate-500">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Computing live ETAs…
                </span>
              )}
              {!etasLoading && closestTruckId && (
                <span className="flex items-center gap-1 text-[11px] text-emerald-700 font-semibold">
                  <Trophy className="h-3 w-3" />
                  Sorted by closest
                </span>
              )}
            </div>
            <Select value={truckId} onValueChange={setTruckId}>
              <SelectTrigger data-testid="assign-truck-select">
                <SelectValue placeholder="Select a truck..." />
              </SelectTrigger>
              <SelectContent>
                {sortedTrucks.length === 0 && (
                  <div className="px-3 py-2 text-sm text-slate-500">No on-duty trucks</div>
                )}
                {sortedTrucks.map((t) => (
                  <TruckOption
                    key={t.id}
                    truck={t}
                    eta={etas[t.id]}
                    queueSize={queueSizeFor(t.id)}
                    isClosest={t.id === closestTruckId}
                  />
                ))}
              </SelectContent>
            </Select>
            {truckId && selectedEta && (
              <div
                className="mt-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-md px-2.5 py-1.5 flex items-center gap-1.5"
                data-testid="assign-eta-notice"
              >
                <Route className="h-3.5 w-3.5" />
                ETA to pickup: <strong>{formatDuration(selectedEta.duration_s)}</strong>
                <span className="text-emerald-700">· {formatDistance(selectedEta.distance_m)}</span>
              </div>
            )}
            {truckId && selectedQueueSize > 0 && (
              <div
                className="mt-2 text-xs bg-blue-50 border border-blue-200 text-blue-800 rounded-md px-2.5 py-1.5"
                data-testid="assign-queue-notice"
              >
                This truck already has <strong>{selectedQueueSize}</strong> job{selectedQueueSize > 1 ? "s" : ""} queued.
                This will be added as position <strong>#{selectedQueueSize + 1}</strong>.
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          {/* Return to Queue — only on reassign. Bottom-left of the footer,
           * separated from the Cancel/Confirm cluster so a dispatcher won't
           * fat-finger it. */}
          {isReassign ? (
            <Button
              variant="outline"
              onClick={returnToQueue}
              disabled={saving}
              className={cn(
                "border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800",
                willBookForLater && "border-emerald-300 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-950/30",
              )}
              data-testid="assign-return-to-queue-btn"
            >
              {willBookForLater ? <CalendarClock className="h-4 w-4" /> : <Undo2 className="h-4 w-4" />}
              {willBookForLater ? "Book for Later" : "Return to Queue"}
            </Button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="assign-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={submit}
              disabled={saving || !truckId}
              className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
              data-testid="assign-confirm-btn"
            >
              {saving ? (
                <><Loader2 className="h-4 w-4 animate-spin" />{isReassign ? "Reassigning…" : "Assigning…"}</>
              ) : (
                isReassign ? "Confirm Reassignment" : "Confirm Assignment"
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
